import { navigateTo, useRoute, useRouter, useState } from '#imports'
import { computed, effectScope, watch } from 'vue'
import type { LocationQuery } from 'vue-router'

import { useCampaignRouteName } from '~/scopes/campaign/composables/useCampaignRouteName'
import {
  IMPACT_ROUTES,
  MODAL_ROUTES,
  type ModalRoute,
  impactRoute,
  isImpactRoute,
  routeImpact,
} from '~/scopes/campaign/const/config.const'
import {
  type Article,
  articleBySlug,
} from '~/scopes/campaign/data/articles/data'
import {
  FOOTER_DOCS,
  type FooterDocId,
} from '~/scopes/campaign/data/footer-docs'
import {
  journeyFor,
  stopFromToken,
  stopToken,
} from '~/scopes/campaign/data/journeys/data'
import type { ImpactType } from '~/scopes/campaign/data/metals/types'
import { RAW_MATERIALS } from '~/scopes/campaign/route-names'
import { dewebviewifyRouteName } from '~/scopes/webviews/utils'

export type ModalState = ModalRoute

/**
 * The extended-modal tier: every value `?view=` can take, i.e. every consumer of
 * `UI/ExtendedModal.vue`. `article` and `metal` — the two readers — name a presentation
 * mode over whatever the path (or `?metal=`) already points at; the rest name themselves.
 *
 * This list is the whole registry — the type is read off it, so adding a card is adding
 * one value here, and an id present is a promise that `?view=<id>` renders something.
 * The footer's documents are spread from their own registry, so authoring a fourth
 * document never touches this file.
 *
 * `'metal'` collides in spelling only with `MODAL_ROUTES.METAL_LIBRARY`: one is a value of
 * `?view=`, the other of `route.params.type`. Different keys, no shared parsing.
 */
const EXTENDED_VIEWS = [
  'article',
  'metal',
  'action',
  'mp-letter',
  ...FOOTER_DOCS.map((doc) => doc.id),
] as const

export type ExtendedView = (typeof EXTENDED_VIEWS)[number]

// Shared across every caller: the most recent impact route the user viewed.
// Lets the desktop impacts panel keep showing it while another overlay is open,
// and lets closing that overlay return to it in the URL.

let tracking: ReturnType<typeof effectScope> | null = null

export function useModalState() {
  const lastImpact = useState<ModalRoute | null>('lastImpact', () => null)
  const route = useRoute()
  const router = useRouter()
  const routeName = useCampaignRouteName()

  // This route's campaign name, webview suffix stripped.
  const currentRoute = computed(() =>
    dewebviewifyRouteName((route.name as string | undefined) ?? ''),
  )

  // Article slug or metal slug — both kebab-case, so gate on the route name, never on
  // the value's shape.
  const activeDetail = computed<string | null>(() => {
    const param = route.params.id
    const value = Array.isArray(param) ? param[0] : param

    return value || null
  })

  /** The story the path names, or null off the article route. */
  const activeArticle = computed(() =>
    currentRoute.value === RAW_MATERIALS.ARTICLE
      ? articleBySlug(activeDetail.value)
      : null,
  )

  const activeModal = computed<ModalRoute | null>(() => {
    const name = currentRoute.value

    // The journey is included: it still names an impact in `params.type`, so
    // `activeModal` keeps resolving beneath it and `lastImpact` keeps tracking.
    if (name === RAW_MATERIALS.IMPACT || name === RAW_MATERIALS.IMPACT_MINING) {
      const type = Array.isArray(route.params.type)
        ? route.params.type[0]
        : route.params.type

      return (Object.values(MODAL_ROUTES) as string[]).includes(type)
        ? (type as ModalRoute)
        : null
    }

    // An article's path carries no impact, so the panel behind it is inferred: the
    // one you came from if the story speaks to it, else the story's primary.
    if (name === RAW_MATERIALS.ARTICLE) {
      const impacts = activeArticle.value?.impactList ?? []
      const routes = impacts.map(impactRoute)

      return routes.find((r) => r === lastImpact.value) ?? routes[0] ?? null
    }

    if (name === RAW_MATERIALS.METAL_DETAIL || name === RAW_MATERIALS.METAL)
      return MODAL_ROUTES.METAL_LIBRARY
    if (name === RAW_MATERIALS.CONTENT_GALLERY)
      return MODAL_ROUTES.CONTENT_GALLERY
    if (name === 'campaign-take-action') return MODAL_ROUTES.TAKE_ACTION

    return null
  })

  // A metal "quick view" layered on any route via `?metal=<slug>`. Distinct
  // from the `/metal/:id` library route — opened from map markers and article
  // links, it closes back to the current route without revealing the list.
  const quickMetal = computed<string | null>(() => {
    const param = route.query.metal
    const value = Array.isArray(param) ? param[0] : param

    return value || null
  })

  // Which single mine the quick view zoomed to, layered alongside `?metal=` via
  // `?mine=<mineSlug>`. Only map-marker taps set it (article chips open the metal
  // without a mine); the map flies to that mine and falls back to framing all of
  // the metal's mines when it's absent or unresolvable.
  const quickMine = computed<string | null>(() => {
    const param = route.query.mine
    const value = Array.isArray(param) ? param[0] : param

    return value || null
  })

  // "Cinema mode" for a self-hosted video, layered via `?video=<id>`. The inline
  // player keeps playing decoratively underneath — the param only marks which one has
  // grown to fill the screen. One key, so only one video ever holds the screen; the
  // instance that owns the id is settled in `UI/NativeVideo.vue`.
  const activeCinema = computed<string | null>(() => {
    const param = route.query.video
    const value = Array.isArray(param) ? param[0] : param

    return value || null
  })

  // The extended view — the wide centred card (`UI/ExtendedModal.vue`) — layered on any
  // route via `?view=<id>`. One key for the whole tier, so **the exclusivity is the URL's**:
  // a key holds one value, which is what "only one ExtendedModal is ever open" rests on.
  // Nothing has to remember to close its siblings, and adding a consumer is adding a value.
  // (Not to be confused with the metal *quick view*, `?metal=` — a different layer, below.)
  //
  // The two readers carry their own value rather than sharing one flag, even though they
  // share a card: the value selects the branch, so `/metal/gold?view=article` simply
  // renders nothing instead of leaning on a route guard to stop a metal being rendered
  // as an article.
  const activeView = computed<ExtendedView | null>(() => {
    const param = route.query.view
    const value = Array.isArray(param) ? param[0] : param
    // Validated, unlike `?metal=`: this key gates `ResponsiveModal`'s Escape ladder, so
    // an unknown value has to read as closed. Left raw, `?view=whatever` would render
    // nothing and still swallow the Escape of a panel that *is* open.
    return EXTENDED_VIEWS.includes(value as ExtendedView)
      ? (value as ExtendedView)
      : null
  })

  // Named views for the consumers, so nothing outside this file spells a URL value. The
  // two readers — one shared card, `ExtendedReader`, with two subjects — are the
  // article's and the metal's; take action is also reachable as a route
  // (`MODAL_ROUTES.TAKE_ACTION`), but every trigger uses the layered form so pressing one
  // never navigates away from what's underneath. The footer documents need no equivalent:
  // their adapter resolves `activeView` against the document registry, which answers
  // "which document" and "is it one at all" in the same call.
  const activeReader = computed(() => activeView.value === 'article')
  const activeMetalReader = computed(() => activeView.value === 'metal')
  const activeAction = computed(() => activeView.value === 'action')
  // Take action's second branch: the value moves, the card stays up.
  const activeMpLetter = computed(() => activeView.value === 'mp-letter')

  // Guided mining is a route, not a layered param: it replaces the panels rather
  // than covering them. See `.claude/rules/guided-mining.md`.
  const isJourneyRoute = computed(() => {
    const name = route.name as string | undefined

    return !!name && dewebviewifyRouteName(name) === RAW_MATERIALS.IMPACT_MINING
  })

  // `null` unless a journey is authored for that impact, not merely routed to.
  const activeJourney = computed<ImpactType | null>(() => {
    if (!isJourneyRoute.value) return null
    const param = route.params.type
    const value = Array.isArray(param) ? param[0] : param
    const impact = routeImpact(value)

    return journeyFor(impact) ? impact : null
  })

  // The 1-based index behind the URL's token — `intro`, the counter's own numbers,
  // `outro` — validated against this journey's own length.
  const journeyStep = computed<number | null>(() => {
    const journey = journeyFor(activeJourney.value)
    if (!journey) return null
    const param = route.params.step
    const value = Array.isArray(param) ? param[0] : param

    return value ? stopFromToken(journey, value) : null
  })

  // Track the active impact once, in a detached scope so it lives for the
  // app's lifetime regardless of which component first calls this composable.
  // A watcher (not the navigate* helpers) catches every entry — clicks,
  // deep links and browser navigation alike.
  if (!tracking) {
    tracking = effectScope(true)
    tracking.run(() => {
      watch(
        activeModal,
        (modal) => {
          if (isImpactRoute(modal)) {
            lastImpact.value = modal
          }
        },
        { immediate: true },
      )
    })
  }

  function navigate(state: ModalRoute) {
    if (IMPACT_ROUTES.includes(state)) {
      navigateTo({ name: routeName('IMPACT'), params: { type: state } })
    } else if (state === MODAL_ROUTES.METAL_LIBRARY) {
      navigateTo({ name: routeName('METAL') })
    } else if (state === MODAL_ROUTES.CONTENT_GALLERY) {
      navigateTo({ name: routeName('CONTENT_GALLERY') })
    } else if (state === MODAL_ROUTES.TAKE_ACTION) {
      navigateTo({ name: routeName('TAKE_ACTIONS') })
    }
  }

  // `mine` (metal library only) rides along as `?mine=<mineSlug>` so a marker tap
  // that deepens into the library detail still tells the map which single mine to
  // fly to. List clicks pass none and frame all the metal's mines.
  //
  // Everything else in the query is dropped on purpose — a `?metal=` quick view must
  // not survive into a different subject. The reader is the one exception: it's a
  // presentation mode the user chose, not a layer bound to the old subject, so a
  // related story or related metal opened from the reader stays in the reader. Carrying
  // it here (rather than re-`replace`ing after the push) keeps it to one navigation, so
  // the reader never blinks shut for a frame in between — and each branch carries only
  // *its own* value, since a reader whose subject the new route can't supply would open
  // on nothing.
  // Either reader hands off, not only the matching one: a related story opened from the
  // metal reader still wants a reader, and the impact route can supply it. The library
  // branch stays narrower — `/metal/:slug` can't supply an article reader, so carrying
  // `article` there would open a card on nothing.
  function navigateToDetail(modal: ModalRoute, id: string, mine?: string) {
    if (modal === MODAL_ROUTES.METAL_LIBRARY) {
      const query: LocationQuery = {}
      if (mine) query.mine = mine
      if (activeMetalReader.value) query.view = 'metal'
      navigateTo({
        name: routeName('METAL_DETAIL'),
        params: { id },
        query,
      })
    }
  }

  // Takes the article, not an id and an impact: the path is built from the story alone,
  // so list, related rail and map marker all reach the same URL.
  function navigateToArticle(article: Article) {
    navigateTo({
      name: routeName('ARTICLE'),
      params: { metal: article.metal.slug, id: article.slug },
      query:
        activeReader.value || activeMetalReader.value
          ? { view: 'article' }
          : {},
    })
  }

  function navigateToHome() {
    // Going home forgets the remembered impact, so closing a later overlay
    // on mobile won't resurrect a sheet the user already collapsed. Desktop
    // still shows a default impact (activeImpact falls back to environmental).
    lastImpact.value = null
    navigateTo({ name: routeName('HOME') })
  }

  // Closing an overlay (metals/content/take-action) returns to the impact the
  // user was last viewing, falling back to home if they never opened one.
  function closeToImpacts() {
    if (lastImpact.value) {
      navigate(lastImpact.value)
    } else {
      navigateToHome()
    }
  }

  // Closing inside the metals library is two-level: a metal detail
  // (`/metal/:id`) steps back to the list (`/metal`), and only closing the
  // list itself leaves to the impacts the user came from. The `?metal=` quick
  // view (map markers, article links) is separate — it has no list beneath,
  // so it closes straight back to its route via `closeMetalQuickView`.
  function closeMetals() {
    if (activeDetail.value) {
      navigate(MODAL_ROUTES.METAL_LIBRARY)
    } else {
      closeToImpacts()
    }
  }

  // Open a metal quick view: layer `?metal=<slug>` on the current route, plus
  // `?mine=<mineSlug>` when opened from a specific map marker (so the map flies to
  // that one mine). Article chips pass no mine — the map then frames all the metal's
  // mines. If the metals library is already open, deepen its own list→detail flow
  // (carrying the mine, so a marker tap still flies to it) instead of stacking a
  // parallel overlay. Tapping the same metal+mine closes it — except from an extended
  // view, see below.
  function openMetalQuickView(slug: string, mine?: string) {
    // Tapping the same metal+mine closes it — but never from under an extended view,
    // where the quick view is hidden behind the backdrop, so a chip tap can only
    // mean "show me this metal". Skipping the toggle there falls through to the
    // normal path, which either swaps the reader onto this metal or drops `view` and
    // reveals the panel. One navigation, and the same end state whether or not it was
    // already open. (The guard is on the tier because the reason is the backdrop.)
    if (
      !activeView.value &&
      quickMetal.value === slug &&
      quickMine.value === (mine ?? null)
    ) {
      closeMetalQuickView()

      return
    }
    if (activeModal.value === MODAL_ROUTES.METAL_LIBRARY) {
      navigateToDetail(MODAL_ROUTES.METAL_LIBRARY, slug, mine)

      return
    }
    const query: LocationQuery = { ...route.query, metal: slug }
    if (mine) query.mine = mine
    else delete query.mine
    // From a reader the metal opens *as* the metal reader — the card stays up and
    // crossfades in place — so this writes the value instead of dropping the key.
    // `?metal=` is doing double duty there: it opens the quick view underneath *and*
    // is where the reader reads its subject from, an impact route having nowhere else
    // to say which metal. From any other extended view (take action, a footer
    // document) the chip still trades the card for the quick view.
    if (activeReader.value || activeMetalReader.value) query.view = 'metal'
    else delete query.view
    navigateTo({ query }, { replace: true })
  }

  // Close the quick view: drop `?metal` and `?mine`, staying on the current route.
  function closeMetalQuickView() {
    const query = { ...route.query }
    delete query.metal
    delete query.mine
    navigateTo({ query }, { replace: true })
  }

  // Enter cinema mode: layer `?video=<id>` on the current route.
  function openCinema(id: string) {
    navigateTo({ query: { ...route.query, video: id } }, { replace: true })
  }

  // Leave cinema mode: drop `?video`, staying on the current route.
  function closeCinema() {
    const query = { ...route.query }
    delete query.video
    navigateTo({ query }, { replace: true })
  }

  // Open an extended view over whatever is on screen: write the one key, and whatever was
  // in it is gone. That single assignment *is* the "only one ExtendedModal is ever open"
  // invariant — no sibling params to clear, which is what would otherwise need a z ladder
  // and an Escape ladder to match. It also never navigates: the article, metal or panel
  // underneath stays mounted, so closing lands back exactly where the user was.
  function openView(view: ExtendedView) {
    navigateTo({ query: { ...route.query, view } }, { replace: true })
  }

  // Close it: drop `?view`, staying on the current route. One closer for the whole tier —
  // closing has no subject, and the route beneath already says what is revealed (the
  // article's own path for the reader, whatever the footer was floating over for a
  // document).
  function closeView() {
    const query = { ...route.query }
    delete query.view
    navigateTo({ query }, { replace: true })
  }

  // Named openers, so no consumer spells a URL value. No toggle on either reader: the
  // extend button is unreachable while the card covers it.
  const openReader = () => openView('article')
  const openMetalReader = () => openView('metal')
  const openAction = () => openView('action')
  const openMpLetter = () => openView('mp-letter')
  const openDoc = (id: FooterDocId) => openView(id)

  // The one place a stop becomes a path: every caller passes the index.
  function journeyLocation(impact: ImpactType, step: number) {
    const journey = journeyFor(impact)

    return {
      name: routeName('IMPACT_MINING'),
      params: {
        type: impact,
        step: journey ? stopToken(journey, step) : 'intro',
      },
    }
  }

  function openJourney(impact: ImpactType) {
    navigateTo(journeyLocation(impact, 1))
  }

  // Stops push, not replace: the browser's Back should walk a sequence backwards.
  function goToStop(step: number) {
    const impact = activeJourney.value
    if (impact) navigateTo(journeyLocation(impact, step))
  }

  // Go through the browser when the entry behind us *is* the previous stop, so its
  // Back and this button stay one move; otherwise (a cold deep link) push.
  function journeyBack() {
    const impact = activeJourney.value
    const step = journeyStep.value
    if (!impact || !step || step <= 1) return
    const previous = router.resolve(journeyLocation(impact, step - 1))
    const back =
      typeof window !== 'undefined' ? window.history.state?.back : null

    if (back === previous.fullPath) router.back()
    else navigateTo(previous.fullPath)
  }

  // Pushes rather than unwinding: `router.go(-N)` is unverifiable and wrong for a
  // deep link. Cost: one Back from the panel returns to the stop just left.
  function exitJourney() {
    const impact = activeJourney.value
    if (impact) navigate(impactRoute(impact))
    else navigateToHome()
  }

  function finishJourney() {
    const journey = journeyFor(activeJourney.value)
    if (!journey) return
    navigateTo({
      name: routeName('METAL_DETAIL'),
      params: { id: journey.metal.slug },
      query: { view: 'metal' },
    })
  }

  return {
    activeModal,
    activeDetail,
    activeArticle,
    activeCinema,
    activeView,
    activeReader,
    activeMetalReader,
    activeAction,
    activeMpLetter,
    lastImpact,
    quickMetal,
    quickMine,
    navigate,
    navigateToDetail,
    navigateToArticle,
    navigateToHome,
    closeToImpacts,
    closeMetals,
    openMetalQuickView,
    closeMetalQuickView,
    openCinema,
    closeCinema,
    openView,
    closeView,
    openReader,
    openMetalReader,
    openAction,
    openMpLetter,
    openDoc,
    activeJourney,
    journeyStep,
    openJourney,
    goToStop,
    journeyBack,
    exitJourney,
    finishJourney,
  }
}
